/**
 * 表单验证规则合集
 * 包含各种常用的验证规则函数
 */

import langMap from './language';

/**
 * 密码验证规则
 * @param {Object} rule - 验证规则对象
 * @param {String} value - 待验证的值
 * @param {Function} callback - 回调函数
 * @param {String} lang - 语言类型，默认为'zh'
 */
export const validatePassword = (rule, value, callback, lang = 'zh') => {
  // 检查密码长度是否至少为8位
  if (value.length < 8) {
    callback(new Error(langMap.minEightChar[lang]));
    return;
  }

  // 检查密码是否包含至少一个英文字母
  if (!/[a-zA-Z]/.test(value)) {
    callback(new Error(langMap.minOneLetter[lang]));
    return;
  }

  // 检查密码是否包含超过3位的连续数字序列（正序和倒序）
  for (let i = 0; i < value.length - 3; i++) {
    const segment = value.substring(i, i + 4);
    // 检查是否为连续递增或递减数字
    if (/^\d{4,}$/.test(segment)) {
      // 检查正序连续
      let isConsecutiveAsc = true;
      for (let j = 0; j < segment.length - 1; j++) {
        if (parseInt(segment[j]) + 1 !== parseInt(segment[j + 1])) {
          isConsecutiveAsc = false;
          break;
        }
      }

      // 检查倒序连续
      let isConsecutiveDesc = true;
      for (let j = 0; j < segment.length - 1; j++) {
        if (parseInt(segment[j]) - 1 !== parseInt(segment[j + 1])) {
          isConsecutiveDesc = false;
          break;
        }
      }

      if (isConsecutiveAsc || isConsecutiveDesc) {
        callback(new Error(langMap.maxThreeConsecutive[lang]));
        return;
      }
    }
  }

  // 检查密码是否包含超过4位相同的相邻字符
  for (let i = 0; i < value.length - 4; i++) {
    const char = value[i];
    let count = 1;

    for (let j = i + 1; j < value.length; j++) {
      if (value[j] === char) {
        count++;
        if (count > 4) {
          callback(new Error(langMap.maxFourRepeated[lang]));
          return;
        }
      } else {
        break; // 遇到不同字符，结束当前检查
      }
    }
  }

  callback();
};

/**
 * 手机号验证规则
 * @param {Object} rule - 验证规则对象
 * @param {String} value - 待验证的值
 * @param {Function} callback - 回调函数
 * @param {String} lang - 语言类型，默认为'zh'
 */
export const validatePhone = (rule, value, callback, lang = 'zh') => {
  if (!value) {
    callback(new Error(langMap.pleaseInputPhone[lang]));
    return;
  }
  
  if (value.length !== 11) {
    callback(new Error(langMap.phoneFormatError[lang]));
    return;
  }
  
  if (!/^[0-9]*$/.test(value)) {
    callback(new Error(langMap.phoneInputError[lang]));
    return;
  }
  
  callback();
};

/**
 * 邮箱验证规则
 * @param {Object} rule - 验证规则对象
 * @param {String} value - 待验证的值
 * @param {Function} callback - 回调函数
 * @param {String} lang - 语言类型，默认为'zh'
 */
export const validateEmail = (rule, value, callback, lang = 'zh') => {
  if (!value) {
    callback(new Error(langMap.pleaseInputEmail[lang]));
    return;
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    callback(new Error(langMap.emailFormatError[lang]));
    return;
  }
  
  callback();
};

/**
 * 用户名验证规则
 * @param {Object} rule - 验证规则对象
 * @param {String} value - 待验证的值
 * @param {Function} callback - 回调函数
 * @param {String} lang - 语言类型，默认为'zh'
 */
export const validateUsername = (rule, value, callback, lang = 'zh') => {
  if (!value) {
    callback(new Error(langMap.pleaseInputUsername[lang]));
    return;
  }
  
  if (value.length < 2) {
    callback(new Error(langMap.minTwoChar[lang] || '用户名至少需要2个字符'));
    return;
  }
  
  if (value.length > 20) {
    callback(new Error(langMap.maxTwentyChar[lang] || '用户名不能超过20个字符'));
    return;
  }
  
  callback();
};

/**
 * 账号验证规则
 * @param {Object} rule - 验证规则对象
 * @param {String} value - 待验证的值
 * @param {Function} callback - 回调函数
 * @param {String} lang - 语言类型，默认为'zh'
 */
export const validateAccount = (rule, value, callback, lang = 'zh') => {
  if (!value) {
    callback(new Error(langMap.pleaseInputAccount[lang]));
    return;
  }
  
  if (value.length < 3) {
    callback(new Error(langMap.minThreeChar[lang] || '账号至少需要3个字符'));
    return;
  }
  
  if (value.length > 20) {
    callback(new Error(langMap.maxTwentyChar[lang] || '账号不能超过20个字符'));
    return;
  }
  
  // 只允许字母、数字、下划线
  if (!/^[a-zA-Z0-9_]+$/.test(value)) {
    callback(new Error(langMap.accountFormatError[lang] || '账号只能包含字母、数字和下划线'));
    return;
  }
  
  callback();
};

/**
 * 验证码验证规则
 * @param {Object} rule - 验证规则对象
 * @param {String} value - 待验证的值
 * @param {Function} callback - 回调函数
 * @param {String} lang - 语言类型，默认为'zh'
 */
export const validateVerificationCode = (rule, value, callback, lang = 'zh') => {
  if (!value) {
    callback(new Error(langMap.pleaseInputVerificationCode[lang]));
    return;
  }
  
  if (value.length !== 6) {
    callback(new Error(langMap.codeFormatError[lang] || '验证码必须是6位数字'));
    return;
  }
  
  if (!/^\d{6}$/.test(value)) {
    callback(new Error(langMap.codeFormatError[lang] || '验证码必须是6位数字'));
    return;
  }
  
  callback();
};

/**
 * 创建带语言参数的验证器工厂函数
 * @param {Function} validator - 验证器函数
 * @param {String} lang - 语言类型
 * @returns {Function} 返回绑定了语言参数的验证器函数
 */
export const createValidator = (validator, lang) => {
  return (rule, value, callback) => {
    return validator(rule, value, callback, lang);
  };
};

/**
 * 验证器集合对象，方便批量导入
 */
export const validators = {
  validatePassword,
  validatePhone,
  validateEmail,
  validateUsername,
  validateAccount,
  validateVerificationCode,
  createValidator
};

export default validators;
